import { useEffect, useState } from "react";
import { Users, Radio, GraduationCap, ArrowLeftRight, BookOpen, Scale, FileWarning, FileSearch, SlidersHorizontal, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { DEMO_CONFIG } from "./mdtDemo";
import RosterTab from "./RosterTab";
import RadioTab from "./RadioTab";
import ExamsTab from "./ExamsTab";
import TransfersTab from "./TransfersTab";
import SopsTab from "./SopsTab";
import LawsTab from "./LawsTab";
import WarrantsTab from "./WarrantsTab";
import SubpoenasTab from "./SubpoenasTab";
import ConfigTab from "./ConfigTab";

/* Department Ops home inside the MDT — roster/ranks, radio directory, exams,
   transfers, SOPs, penal code, warrants, subpoenas, and the command structure editor.
   Every tab renders for all units; editing is gated by supervisor/command perms. */

const TABS = [
  ["roster", Users, "Roster", () => true],
  ["radio", Radio, "Radio", () => true],
  ["exams", GraduationCap, "Exams", () => true],
  ["transfers", ArrowLeftRight, "Transfers", () => true],
  ["sops", BookOpen, "SOPs", () => true],
  ["laws", Scale, "Laws", () => true],
  ["warrants", FileWarning, "Warrants", () => true],
  ["subpoenas", FileSearch, "Subpoenas", (me) => me?.is_supervisor],
  ["structure", SlidersHorizontal, "Structure", (me) => me?.is_command],
];

export default function DeptView({ serverId, token, demo, sfx, me, setMe, tab, setTab }) {
  const [localTab, setLocalTab] = useState("roster");
  const activeTab = tab ?? localTab;
  const selectTab = (v) => { (setTab ?? setLocalTab)(v); };
  const [config, setConfig] = useState(demo ? DEMO_CONFIG : null);

  const refreshMe = () => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/cad/dept/me`, { params: { token } })
      .then((r) => setMe(r.data)).catch(() => {});
  };
  const refreshConfig = () => {
    if (demo) { setConfig({ ...DEMO_CONFIG }); return; }
    api.get(`/nc/servers/${serverId}/cad/dept/config`, { params: { token } })
      .then((r) => setConfig(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshConfig(); }, []);

  if (!me || !config) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-400" /></div>;

  const G = { serverId, token, demo, sfx, me, config, refreshMe, refreshConfig };
  const visible = TABS.filter(([, , , ok]) => ok(me));

  return (
    <div data-testid="dept-view" className="flex-1 flex flex-col min-h-0">
      {/* identity strip */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-white/10 bg-black/40 font-mono text-[9px] uppercase tracking-[0.2em]">
        <span className="text-zinc-500">Signed in:</span>
        <span data-testid="dept-identity" className="text-sky-300 font-bold">
          {me.name} · {me.rank?.name || "Unranked"}{me.dept ? ` · ${me.dept.name}` : ""}
        </span>
        {me.is_supervisor && <span className="text-amber-300 border border-amber-500/40 px-1.5 py-0.5">Supervisor</span>}
        {me.is_command && <span className="text-red-300 border border-red-500/40 px-1.5 py-0.5">Command</span>}
        {me.bootstrap && !demo && <span className="text-zinc-500">bootstrap: assign command ranks to lock down</span>}
        <span className="ml-auto text-zinc-600">{config.departments.length} departments · per-server structure</span>
      </div>
      {/* tab bar */}
      <div className="flex border-b border-white/10 bg-black/30 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
        {visible.map(([v, Icon, l]) => (
          <button key={v} data-testid={`dept-tab-${v}`} onClick={() => { selectTab(v); sfx.keyTick(); }}
            className={`flex items-center gap-1.5 px-3.5 py-2 font-mono text-[9px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${activeTab === v ? "text-sky-300 border-b-2 border-sky-400 bg-sky-500/5" : "text-zinc-500 hover:text-zinc-200"}`}>
            <Icon className="w-3 h-3" />{l}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        {activeTab === "roster" && <RosterTab G={G} />}
        {activeTab === "radio" && <RadioTab G={G} />}
        {activeTab === "exams" && <ExamsTab G={G} />}
        {activeTab === "transfers" && <TransfersTab G={G} />}
        {activeTab === "sops" && <SopsTab G={G} />}
        {activeTab === "laws" && <LawsTab G={G} />}
        {activeTab === "warrants" && <WarrantsTab G={G} />}
        {activeTab === "subpoenas" && <SubpoenasTab G={G} />}
        {activeTab === "structure" && <ConfigTab G={G} />}
      </div>
    </div>
  );
}
